import React, { useState, useCallback } from "react";
import { Page, LegacyCard, IndexTable, Layout, InlineStack } from "@shopify/polaris";
import { ImportIcon, ExportIcon } from "@shopify/polaris-icons";
import qs from "query-string";
import { TimeMachineModal } from "./support/TimeMachineModal";
import { ImportFile } from "../../common/ImportFile";
import { HorseVariantSearchFieldAndMoreFilters } from "./support/HorseVariantSearchFieldAndMoreFilters";
import ImportProgressBanner from "../../home/support/ImportProgressBanner";
import {
  importHorseVariants,
  downloadFile,
  useUser,
  useHorseVariants,
  useHorseVariantsTotals,
  useHorseLocationsOptions,
  processQueryParams,
} from "../../../api_utils/requests";
import { formatMoney, commaDelimiter, softAssertNumber } from "../../../helper_functions/utils";
import { ErrorBanner } from "../../common/ErrorBanner";
import { Footer } from "../../common/Footer";
import PopoverWithCheckList from "../../common/Filters/PopoverWithCheckList";
import { HorseVariantsIndexPageSkeleton } from "../../common/skeletons";
import {
  HORSE_VARIANTS_TABS as TABS,
  HORSE_VARIANTS_COLLECTIONS as COLLECTIONS,
  type HorseVariantsQueryParams,
} from "../../../api_utils/types";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import { HorseVariantRow } from "../../common/HorseVariant/HorseVariantRow";
import { LocationColumnHeading } from "./support/LocationColumnHeading";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useVisibleColumns } from "../../../hooks/useVisibleColumns";
import { variantColumns, type VariantColumn } from "./optionalColumns";

const dateOptions = { year: "numeric", month: "long", day: "numeric" } satisfies Intl.DateTimeFormatOptions;

const horseVariantHelpPageUrl =
  "https://horse-inventory.notion.site/Variants-directory-da6d069d1f6e4755a91c1dd95c04b303";

function HorseVariants(): React.ReactElement {
  const [errorMessage, setErrorMessage] = useState("");

  const [importCSVModalIsOpen, setImportCSVModalIsOpen] = useState(false);

  const parsedUrlSearch = qs.parse(window.location.search);
  const [filters, setFilters] = useState<HorseVariantsQueryParams>(parsedUrlSearch);
  const [pageNum, setPageNum] = useState(Number(parsedUrlSearch.page) || 1);
  const defaultCollection = TABS.UNDISCARDED;
  const [collection, setCollection] = useState<number>(
    softAssertNumber(parsedUrlSearch.collection as string) || TABS.UNDISCARDED,
  );
  const [date, setDate] = useState<Date | undefined>(
    parsedUrlSearch.date ? new Date(parsedUrlSearch.date as string) : undefined,
  );
  const pageTitle = date ? `Inventory on ${date.toLocaleDateString(undefined, dateOptions)}` : "Variants";

  const [horseLocationIds, setHorseLocationIds] = useState<string[]>(
    new URLSearchParams(window.location.search).getAll("stocked_at_horse_location_ids[]"),
  );
  const horseLocationStockFilter = { stocked_at_horse_location_ids: horseLocationIds };
  const { data: horseLocations } = useHorseLocationsOptions({ omitAll: true });
  const columns: VariantColumn[] = [
    ...variantColumns,
    ...horseLocations.map((horseLocation): VariantColumn => ({
      id: `location:${horseLocation.value}`,
      title: horseLocation.label,
      horseLocationId: horseLocation.value,
    })),
  ];
  const availableColumns = columns.filter(
    (column) => !("horseLocationId" in column) || horseLocationIds.length === 0 || horseLocationIds.includes(column.horseLocationId),
  );
  const { visibleColumns, selectedColumns, setSelectedColumns } = useVisibleColumns("horse-variants", availableColumns);
  const visibleIncomingColumns = visibleColumns.filter((column) => column.id === "incoming");
  const visibleLocationColumns = visibleColumns.filter((column) => "horseLocationId" in column);
  const visibleOnHandColumns = visibleColumns.filter((column) => column.id === "on_hand");
  const visibleTrailingColumns = visibleColumns.filter(
    (column) => column.id !== "incoming" && column.id !== "on_hand" && !("horseLocationId" in column),
  );
  const inventoryLocationIds = visibleLocationColumns.map((column) => column.horseLocationId);

  const headings = [
    { title: "Product title" },
    { title: "Variant title" },
    { title: "SKU" },
    { title: "Vendor" },
    { title: "Type" },
    ...visibleIncomingColumns,
    { title: "Available", tooltipContent: "The number of units available to sell" },
    ...visibleLocationColumns.map((column) => ({
      id: column.id,
      title: <LocationColumnHeading title={column.title} />,
    })),
    { title: "Committed", tooltipContent: "The number of units set aside for sales orders" },
    ...visibleOnHandColumns,
    { title: "Weight" },
    { title: "Cost" },
    { title: "Value" },
    ...visibleTrailingColumns,
  ] satisfies NonEmptyArray<IndexTableHeading>;

  const {
    data: {
      user: { currency: userCurrency },
    },
    isLoading: pageLoading,
  } = useUser();

  const {
    data: { rows, hasNext, hasPrev },
    isFetching: isFetchingHorseVariants,
    refetch,
  } = useHorseVariants({
    ...filters,
    ...horseLocationStockFilter,
    collection: COLLECTIONS[collection],
    date,
    page: pageNum,
    inventory_location_ids: inventoryLocationIds,
  });

  const isLoading = isFetchingHorseVariants;

  const { data: totals } = useHorseVariantsTotals({
    ...filters,
    ...horseLocationStockFilter,
    collection: COLLECTIONS[collection],
    date,
  });

  let secondaryActions = [];
  let actionGroups = [];
  if (date) {
    secondaryActions = [
      {
        content: "Export CSV",
        onAction: async (): Promise<void> => {
          const filename = `Horse Purchase Orders.csv`;
          const queryParams = {
            ...filters,
            date,
            ...horseLocationStockFilter,
            collection: COLLECTIONS[collection],
          };
          await downloadFile("/horse_variants.csv", filename, queryParams);
        },
        external: true,
      },
    ];
  } else {
    actionGroups = [
      {
        title: "CSV",
        actions: [
          {
            content: "Export",
            onAction: async (): Promise<void> => {
              const filename = `Horse Purchase Orders.csv`;
              const queryParams = {
                ...filters,
                ...horseLocationStockFilter,
                collection: COLLECTIONS[collection],
              };
              await downloadFile("/horse_variants.csv", filename, queryParams);
            },
            icon: ExportIcon,
          },
          {
            content: "Import",
            onAction: () => {
              setImportCSVModalIsOpen(true);
            },
            icon: ImportIcon,
          },
        ],
      },
    ];
  }

  const setPageNumAndScroll = useCallback((newPageNum: number) => {
    setPageNum(newPageNum);
    document.getElementsByClassName("Polaris-IndexTable__Table")[0]?.scrollIntoView();
  }, []);
  const nextPage = useCallback(() => {
    if (hasNext) {
      setPageNumAndScroll(pageNum + 1);
    }
  }, [hasNext, pageNum, setPageNumAndScroll]);
  const prevPage = useCallback(() => {
    if (hasPrev) {
      setPageNumAndScroll(pageNum - 1);
    }
  }, [hasPrev, pageNum, setPageNumAndScroll]);

  const setFiltersAndResetPageNum = useCallback((newFilters: HorseVariantsQueryParams) => {
    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
      setFilters(newFilters);
      setPageNum(1);
    }
  }, [filters]);

  const setCollectionAndResetPageNum = useCallback((newCollection: number) => {
    setCollection(newCollection);
    setPageNum(1);
  }, []);

  const setHorseLocationIdsAndResetPageNum = useCallback((newHorseLocationIds: string[]) => {
    setHorseLocationIds(newHorseLocationIds);
    setPageNum(1);
  }, []);

  const clearHorseLocations = useCallback(() => {
    setHorseLocationIdsAndResetPageNum([]);
  }, [setHorseLocationIdsAndResetPageNum]);

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const onSetSearchParams = useCallback((newQueryParams: HorseVariantsQueryParams) => {
    const newSearchParams = processQueryParams({
      ...newQueryParams,
      stocked_at_horse_location_ids: horseLocationIds,
      collection: collection === defaultCollection ? undefined : COLLECTIONS[collection],
      date,
      page: pageNum === 1 ? undefined : pageNum,
    })
    if (newSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(newSearchParams);
    }
  }, [setSearchParams, collection, defaultCollection, date, horseLocationIds, pageNum, searchParams]);

  const pageMarkup = (
    <Page
      // Have option to go back to variants page when on time machine
      actionGroups={actionGroups}
      backAction={
        !!date && {
          content: pageTitle,
          onAction: () => {
            setDate(undefined);
            setPageNum(1);
            setTimeout(() => {
              navigate(`/horse_variants`);
            });
          },
        }
      }
      fullWidth
      primaryAction={
        <TimeMachineModal
          setDate={(newDate: Date) => {
            setDate(newDate);
            setPageNum(1);
            setTimeout(() => {
              navigate(`/horse_variants?date=${newDate.toISOString()}`);
            });
          }}
        />
      }
      secondaryActions={secondaryActions}
      title={pageTitle}
    >
      <ImportProgressBanner />
      <ImportFile
        apiCallMethod={importHorseVariants}
        modalIsOpen={importCSVModalIsOpen}
        setApiResponse={async (): Promise<void> => {
          await refetch();
        }}
        setErrorMessage={setErrorMessage}
        setModalIsOpen={setImportCSVModalIsOpen}
        title="Import variants CSV"
      />
      <Layout>
        {errorMessage ? (
          <Layout.Section>
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}
        <Layout.Section>
          <LegacyCard>
            <LegacyCard.Section>
              <InlineStack blockAlign="start" gap="300">
                <PopoverWithCheckList
                  choices={horseLocations}
                  defaultTitle="Locations"
                  onChange={setHorseLocationIdsAndResetPageNum}
                  onValueRemoved={(horseLocationId) => {
                    setHorseLocationIdsAndResetPageNum(horseLocationIds.filter((selectedId) => selectedId !== horseLocationId));
                  }}
                  selected={horseLocationIds}
                />
              </InlineStack>
            </LegacyCard.Section>
            <HorseVariantSearchFieldAndMoreFilters
              columnSelection={{
                choices: columns.map((column) => ({
                  value: column.id,
                  label: column.title,
                  disabled: !availableColumns.includes(column),
                })),
                selected: selectedColumns,
                onChange: setSelectedColumns,
              }}
              onClearAll={clearHorseLocations}
              onSelect={setCollectionAndResetPageNum}
              searchParams={searchParams}
              selected={collection}
              setFilters={setFiltersAndResetPageNum}
              setSearchParams={onSetSearchParams}
            />
            <IndexTable
              headings={headings}
              itemCount={rows.length}
              loading={isLoading}
              pagination={{
                hasPrevious: hasPrev,
                previousKeys: [37],
                onPrevious: prevPage,
                hasNext: hasNext,
                nextKeys: [39],
                onNext: nextPage,
              }}
              selectable={false}
            >
              <IndexTable.Row id="horseVariantsIndexTotalsRow" key={0} position={0} rowType="subheader">
                <IndexTable.Cell>Totals</IndexTable.Cell>
                <IndexTable.Cell />
                <IndexTable.Cell />
                <IndexTable.Cell />
                <IndexTable.Cell />
                {visibleIncomingColumns.map((column) => <IndexTable.Cell key={column.id} />)}
                <IndexTable.Cell>{commaDelimiter(totals.currently_available_quantity)}</IndexTable.Cell>
                {visibleLocationColumns.map((column) => <IndexTable.Cell key={column.id} />)}
                <IndexTable.Cell>{commaDelimiter(totals.committed_quantity)}</IndexTable.Cell>
                {visibleOnHandColumns.map((column) => <IndexTable.Cell key={column.id} />)}
                <IndexTable.Cell />
                <IndexTable.Cell />
                <IndexTable.Cell>{formatMoney(totals.total_value, userCurrency, 2)}</IndexTable.Cell>
                {visibleTrailingColumns.map((column) => <IndexTable.Cell key={column.id} />)}
              </IndexTable.Row>
              {rows.map((horseVariant, index) => (
                <HorseVariantRow
                  horseVariant={horseVariant}
                  key={horseVariant.id}
                  position={index + 1}
                  setErrorMessage={setErrorMessage}
                  userCurrency={userCurrency}
                  visibleColumns={visibleColumns}
                />
              ))}
            </IndexTable>
          </LegacyCard>
        </Layout.Section>
      </Layout>
      <Footer pageTitle="variants" url={horseVariantHelpPageUrl} />
    </Page>
  );

  return pageLoading ? <HorseVariantsIndexPageSkeleton /> : pageMarkup;
}

export default HorseVariants;
